import {Dialog, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Text} from '@/components/ui/text/Text'
import {useTranslation} from '@/translations/i18n'
import {StyledHeader} from './style'
import {View, ViewEntity} from '../../types'
import {ViewForm} from '../view-form/ViewForm'
import {zodResolver} from '@hookform/resolvers/zod'
import {CreateViewSchema} from '../../formSchemas'
import {FormProvider, useForm} from 'react-hook-form'
import {AxiosError} from 'axios'
import {errorHandler, raise} from '@/utilities/helpers'
import {useSearchParams} from 'react-router-dom'
import {useDialogContext} from '@ariakit/react'
import {useStoreView} from '../../queries/useStoreView'
import {toast} from '@/utilities/toast/toast'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {useState} from 'react'

export const SaveViewAsNewDialog = ({entity, view}: {entity: ViewEntity; view: View}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)

    return (
        <DialogProvider open={isOpen} setOpen={setIsOpen}>
            <MenuItem
                onClick={e => {
                    e.preventDefault()
                    setIsOpen(true)
                }}
            >
                <PlusIcon />
                {t('commons:view_save_as_new')}
            </MenuItem>
            <Dialog width="400px">
                <StyledHeader>
                    <Text render="h1" size="textXl" weight={500}>
                        {t('commons:view_save_as_new')}
                    </Text>
                </StyledHeader>
                <SaveViewAsNewForm entity={entity} view={view} />
            </Dialog>
        </DialogProvider>
    )
}

const SaveViewAsNewForm = ({entity, view}: {entity: ViewEntity; view: View}) => {
    const {t} = useTranslation()
    const [searchParams, setSearchParams] = useSearchParams()
    const {setOpen} = useDialogContext() ?? raise('Dialog not found')
    const form = useForm<CreateViewSchema>({
        defaultValues: {
            name: view.name,
            visibility: view.visibility
        },
        resolver: zodResolver(CreateViewSchema)
    })

    const storeViewMutation = useStoreView({
        onSuccess: data => {
            setSearchParams(data.body)
            setOpen(false)
            toast.success(t('commons:view_created'))
        },
        onError: error => {
            if (error instanceof AxiosError && error.response?.data.code == 'already-existing-name') {
                form.setError('name', {message: t('errors:view_already_exists')})
            } else {
                errorHandler(error)
            }
        }
    })

    return (
        <FormProvider {...form}>
            <ViewForm
                submitLabel={t('commons:create_view')}
                isPending={storeViewMutation.isPending}
                entity={entity}
                onSubmit={data => {
                    const body = new URLSearchParams(searchParams)
                    body.delete('view')

                    storeViewMutation.mutate({
                        ...data,
                        entity,
                        //Since we need the view id in the search params and we don't have it until the api is success, the &view={{id}} param is sent to backend to be replaced with the actual id
                        body: body.toString() + '&view={{id}}'
                    })
                }}
            />
        </FormProvider>
    )
}
